import { useEffect, useState } from 'react'
import { nanoid } from '@reduxjs/toolkit'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import OutlinedInput from '@mui/material/OutlinedInput'
import Select from '@mui/material/Select'
import InputAdornment from '@mui/material/InputAdornment'
import Snackbar from '@mui/material/Snackbar'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Stack from '@mui/material/Stack'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import KeyIcon from '@mui/icons-material/Key'
import { useTranslation } from 'react-i18next'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import {
  useCreateCameraMutation,
  useGetCameraListsQuery,
  useGetListTypesQuery,
  useUpdateCameraMutation
} from '@/api/camera'
import { useGetChannelsListQuery } from '@/api/channels'
import SnackAlert from '@/components/snack-alert'

// DialogForm.propTypes = {
//   currentId: PropTypes.string.isRequired,
//   open: PropTypes.bool,
//   onClose: PropTypes.func
// }

const DialogForm = ({ open = false, onClose = () => {}, currentId = null }) => {
  const { t } = useTranslation()
  const { data: cameraTypes } = useGetListTypesQuery()
  const [createCamera, { isSuccess, isLoading }] = useCreateCameraMutation()
  const [editCamera, { isSuccess: editSuccess, isLoading: editLoading }] =
    useUpdateCameraMutation()
  const { data: cameras } = useGetCameraListsQuery()
  const { data: channels } = useGetChannelsListQuery()
  const [form, setForm] = useState({
    macAddress: '',
    channelId: null,
    cameraType: null,
    name: '',
    ipAddress: '',
    username: '',
    secret: '',
    directionIn: null,
    token: ''
  })
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [copySnackbar, setCopySnackbar] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const camera = cameras?.find((camera) => camera.id === currentId)

  // const handleChange = ({ target }) => {
  //   if (target.name === 'isActive') {
  //     setForm({ ...form, isActive: !form.isActive })
  //   } else {
  //     setForm({ ...form, [target.name]: target.value })
  //   }
  // }

  const handleChange = ({ target }) => {
    const { name, value } = target

    // if (target.name === 'isActive') {
    //   setForm({ ...form, isActive: !form.isActive })
    // } else {
      const isSecretOrUsernameStars =
        (name === 'secret' || name === 'username') && value === '********'

      if (!isSecretOrUsernameStars) {
        setForm((prevForm) => ({
          ...prevForm,
          [name]: value
        }))
      }
    // }
  }

  const handleClose = () => {
    setForm({
      macAddress: '',
      channelId: null,
      cameraType: null,
      name: '',
      ipAddress: '',
      directionIn: null,
      token: '',
      username: '',
      secret: ''
    })
    onClose()
  }

  const handleSnackOpen = () => {
    setOpenSnackbar(true)
  }

  const handleSnackClose = (event, reason) => {
    if (reason === 'clickaway') return
    setOpenSnackbar(false)
  }

  const onSuccess = () => {
    setForm({
      macAddress: '',
      channelId: null,
      cameraType: null,
      name: '',
      ipAddress: '',
      directionIn: null,
      token: '',
      username: '',
      secret: ''
    })
    handleClose()
    handleSnackOpen()
  }

  const copyToClipBoard = (copyMe) => async () => {
    try {
      await navigator.clipboard.writeText(copyMe)
      setCopySnackbar(true)
    } catch (err) {
      console.log(err)
    }
  }

  const handleSubmit = async () => {
    if (currentId) {
      try {
        await editCamera({
          id: camera?.id,
          mac_address: form.macAddress,
          description: form.name,
          ip_address: form.ipAddress,
          channel_id: form.channelId,
          direction: form.directionIn,
          camera_type_id: form.cameraType,
          access_token: form.token,
          username: form.username !== '********' ? form.username : undefined,
          secret: form.secret !== '********' ? form.secret : undefined
        }).unwrap()
        onSuccess()
      } catch (err) {
        handleClose()
        handleSnackOpen()
      }
    } else {
      try {
        await createCamera({
          mac_address: form.macAddress,
          description: form.name,
          ip_address: form.ipAddress,
          channel_id: form.channelId,
          direction: form.directionIn,
          camera_type_id: form.cameraType,
          access_token: form.token,
          username: form.username,
          secret: form.secret
        }).unwrap()
        onSuccess()
      } catch (err) {
        handleClose()
        handleSnackOpen()
      }
    }
  }

  useEffect(() => {
    if (camera) {
      setForm({
        name: camera?.description,
        macAddress: camera?.mac_address,
        channelId: camera?.channel_id,
        ipAddress: camera?.ip_address,
        directionIn: camera?.direction,
        cameraType: camera?.camera_type_id,
        token: camera?.access_token,
        username: camera?.username ? '********' : '',
        secret: camera?.secret ? '********' : ''
      })
    }
  }, [camera])

  return (
    <>
      <Dialog open={open} fullWidth onClose={handleClose}>
        {(isLoading || editLoading) && <LinearProgress />}
        <DialogTitle>
          {currentId ? t('edit_camera') : t('create_camera')}
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth sx={{ mb: 3, mt: 1 }}>
            <InputLabel>{t('enter_title')}</InputLabel>
            <OutlinedInput
              name="name"
              value={form.name}
              onChange={handleChange}
              label={t('enter_title')}
            />
          </FormControl>
          <FormControl fullWidth sx={{ mb: 3, mt: 1 }}>
            <InputLabel>{t('enter_MAC_address')}</InputLabel>
            <OutlinedInput
              name="macAddress"
              value={form.macAddress}
              onChange={handleChange}
              label={t('enter_MAC_address')}
            />
          </FormControl>
          <FormControl fullWidth sx={{ mb: 3, mt: 1 }}>
            <InputLabel>{t('enter_IP_address')}</InputLabel>
            <OutlinedInput
              name="ipAddress"
              value={form.ipAddress}
              onChange={handleChange}
              label={t('enter_IP_address')}
            />
          </FormControl>
          <Stack
            sx={{
              flexDirection: "row",
              marginBottom: 3,
              marginTop: 1
            }}>
            <FormControl fullWidth sx={{ mr: 1 }}>
              <InputLabel>{t('select_channel')}</InputLabel>
              <Select
                name="channelId"
                autoComplete="channelId"
                value={form?.channelId || 0}
                onChange={handleChange}
                input={<OutlinedInput label={t('select_channel')} />}
              >
                {channels?.map((channel) => (
                  <MenuItem value={channel?.id} key={channel?.id}>
                    {channel?.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{t('select_type')}</InputLabel>
              <Select
                name="cameraType"
                autoComplete="cameraType"
                value={form?.cameraType || ''}
                onChange={handleChange}
                input={<OutlinedInput label={t('select_type')} />}
              >
                {cameraTypes?.map((item) => (
                  <MenuItem value={item?.id} key={item?.id}>
                    {item?.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Stack
            sx={{
              flexDirection: "row",
              mb: 3,
              mt: 1
            }}>
            <FormControl fullWidth sx={{ mr: 1 }}>
              <InputLabel>{t('choose_a_direction')}</InputLabel>
              <Select
                name="directionIn"
                autoComplete="direction"
                value={form.directionIn || ''}
                onChange={handleChange}
                input={<OutlinedInput label={t('choose_a_direction')} />}
              >
                <MenuItem value="in">in</MenuItem>
                <MenuItem value="out">out</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <TextField
                id="input-with-icon-textfield"
                label={t('token')}
                value={form.token}
                disabled
                slotProps={{
                  input: {
                    startAdornment: !currentId && (
                      <InputAdornment position="start">
                        <Tooltip title={t('generate_token')}>
                          <IconButton
                            edge="start"
                            onClick={() =>
                              setForm({ ...form, token: nanoid(64) })
                            }
                          >
                            <KeyIcon />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={t('copy')}>
                          <IconButton
                            edge="end"
                            onClick={copyToClipBoard(form.token)}
                          >
                            <ContentCopyIcon />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    )
                  }
                }}
                variant="outlined"
              />
            </FormControl>
          </Stack>
          <Stack spacing={2} sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Username</InputLabel>
              <OutlinedInput
                name="username"
                type="text"
                value={form.username || ''}
                onChange={handleChange}
                label="Username"
                endAdornment={
                  <InputAdornment position="end">
                    <Tooltip title="Очистить">
                      <IconButton
                        onClick={() => setForm({ ...form, username: '' })} // Очищаем значение
                        edge="end"
                      >
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                }
              />
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Secret</InputLabel>
              <OutlinedInput
                name="secret"
                type={showPassword ? 'text' : 'password'}
                value={form.secret || ''}
                onChange={handleChange}
                label="Secret"
                endAdornment={
                  <InputAdornment position="end">
                    <Tooltip title="Очистить">
                      <IconButton
                        onClick={() => setForm({ ...form, secret: '' })} // Очищаем значение
                        edge="end"
                      >
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={showPassword ? 'Скрыть' : 'Показать'}>
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)} // Логика для скрытия/показа
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                }
              />
            </FormControl>
          </Stack>

          <Button
            sx={{ mt: 3 }}
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={
              !form.name ||
              !form.macAddress ||
              !form.channelId ||
              !form.ipAddress ||
              (!currentId && (!form.username || !form.secret))
            }
          >
            {t('save')}
          </Button>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={copySnackbar}
        autoHideDuration={2000}
        onClose={() => setCopySnackbar(false)}
        message={t('token_copied')}
      />
      <SnackAlert
        open={openSnackbar}
        autoHideDuration={3000}
        severity={isSuccess || editSuccess ? 'success' : 'error'}
        variant="filled"
        message={
          isSuccess || editSuccess
            ? t('change_saved!')
            : t('something_went_wrong!')
        }
        onClose={handleSnackClose}
      />
    </>
  );
}

export default DialogForm
